import { type CSSProperties, type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconBookmarkHeart, IconBookmarkHeartActive } from '@redteclab/icons'

import { Button } from 'base-ui'

import { useOpenableControls } from '../../../generic-hooks'
import { LoginRegistrationUserLoginModal } from '../../../login-registration'
import { useUserApiSwrSessionV1Customer } from '../../../user-data'
import { useLanguageNotepadV2TenantUpidDeleteMutation } from '../../api/swr/useLanguageNotepadV2TenantUpidDeleteMutation'
import { useLanguageNotepadV2TenantUpidGetQuery } from '../../api/swr/useLanguageNotepadV2TenantUpidGetQuery'
import { useLanguageNotepadV2TenantUpidPutMutation } from '../../api/swr/useLanguageNotepadV2TenantUpidPutMutation'
import { useProductPageContext } from '../productPageContext'

export const ProductPageAddToNotepadButton: FC = () => {
  const { product } = useProductPageContext()
  const userSessionData = useUserApiSwrSessionV1Customer()
  const productOnNotepadSwr = useLanguageNotepadV2TenantUpidGetQuery({
    // null will be treated as disabled query - will not make call if not logged in
    upid: userSessionData.data?.customerNumber ? product.upid : null,
  })
  const deleteMutation = useLanguageNotepadV2TenantUpidDeleteMutation()
  const userModalControls = useOpenableControls()
  const putToNotepadMutation = useLanguageNotepadV2TenantUpidPutMutation({
    onSuccess: () => {
      void productOnNotepadSwr.mutate()
    },
  })
  // if no data (not loaded yet), or error (returned 404), then it's not on notepad
  const isProductInNotepad =
    (productOnNotepadSwr.data && !productOnNotepadSwr.error) ?? false

  const handleAddToNotepadIconClick = async (): Promise<void> => {
    if (!userSessionData.data?.customerNumber) {
      userModalControls.handleOpen()

      return
    }

    if (isProductInNotepad) {
      await deleteMutation.trigger({ upid: product.upid })
      void productOnNotepadSwr.mutate()

      return
    }

    void putToNotepadMutation.trigger({ upid: product.upid })
  }

  return (
    <>
      <Button
        data-qa-id="form-add-to-wishlist-tooltip"
        onClick={(): void => {
          void handleAddToNotepadIconClick()
        }}
        size="medium"
        startIcon={
          isProductInNotepad ? (
            <IconBookmarkHeartActive data-qa-id="notepad-filled-icon" />
          ) : (
            <IconBookmarkHeart data-qa-id="notepad-empty-icon" />
          )
        }
        // eslint-disable-next-line react/forbid-component-props -- icon should have text color in this case, UX requirement
        style={{ '--button-icon-color': 'inherit' } as CSSProperties}
        variant="text"
      >
        <FormattedMessage id="general.button.label.memorise" />
      </Button>
      <LoginRegistrationUserLoginModal
        afterLoggedIn={(): void => {
          void putToNotepadMutation.trigger({ upid: product.upid })
          userModalControls.handleClose()
        }}
        onClose={userModalControls.handleClose}
        open={userModalControls.isOpen}
      />
    </>
  )
}
